/**
 * OPERASJONSKATALOGEN FOR AI-EN (revisjonen av MCP-en, Atle 02.11): `GET /v1/ai/operations`.
 *
 * Hver API-operasjon brukeren kan nå, lest av Nests egne metadata ved oppstart — ingen liste skrevet for
 * hånd. MCP-ens to generelle verktøy (`advanced_read`, `advanced_write`; før 02.11 kveld tre, `tvitvi_*`) slår opp her, så
 * en ny rute er tilgjengelig for AI-en i det øyeblikket den finnes, innenfor SPERRENE.
 *
 * Lista er kompakt; skjemaene for kropp og spørring kommer bare for én operasjon (`operationId`).
 */
import { z } from 'zod';

/** `draft_only`: AI-en lagrer et utkast med `POST /drafts`, brukeren sender. */
export const AI_OPERATION_ACCESS = ['allowed', 'approval', 'draft_only', 'never'] as const;
export type AiOperationAccess = (typeof AI_OPERATION_ACCESS)[number];

export const AI_OPERATION_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
export type AiOperationMethod = (typeof AI_OPERATION_METHODS)[number];

/** `binary`: bytene selv (en opplasting), ikke JSON. */
export const AI_OPERATION_BODY_KINDS = ['none', 'json', 'binary'] as const;
/** `file`: ruta skriver bytene selv (PDF, bilde, ICS); `empty`: 204. */
export const AI_OPERATION_RESPONSE_KINDS = ['json', 'file', 'empty', 'redirect'] as const;

export const aiOperationsQuerySchema = z
  .object({
    /** Fritekst over id, sti, sammendrag, ressurs og handling; alle ordene må finnes. */
    q: z.string().trim().min(1).max(200).optional(),
    /** Ressursen (OpenAPI-taggen), f.eks. `booking-register`. */
    resource: z.string().trim().min(1).max(80).optional(),
    /** Én operasjon med skjemaene for kropp og spørring. */
    operationId: z.string().trim().min(1).max(160).optional(),
    access: z.enum(AI_OPERATION_ACCESS).optional(),
    /** `read`: det som bare leser (`reading`), også en POST som en eksport; `write`: resten. */
    kind: z.enum(['read', 'write']).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
  })
  .meta({ id: 'AiOperationsQuery' });
export type AiOperationsQuery = z.infer<typeof aiOperationsQuerySchema>;

export const aiOperationSchema = z.object({
  /** `<controller>.<handler>`, det samme som i `openapi.json` (`tasks.list`). */
  operationId: z.string(),
  method: z.enum(AI_OPERATION_METHODS),
  /** Under `/v1`, med `:param` (`/tasks/:id`). */
  path: z.string(),
  summary: z.string(),
  resource: z.string(),
  /** Det vakta håndhever for et token utenfra: `externalEffectiveAccess`. */
  access: z.enum(AI_OPERATION_ACCESS),
  /**
   * Lesing: GET, eller en POST som bare leser (en eksport, «merk som lest») og derfor er lov også når
   * organisasjonen har slått av «Skrivetilgang for AI» (`isReadingRoute`). Kalles likevel med sin metode.
   */
  reading: z.boolean(),
  /** Handlingen(e) i rollematrisen ruta står på; tom for ruter som er brukerens egne (`/me`). */
  policy: z.array(z.string()),
  pathParams: z.array(z.string()),
  body: z.enum(AI_OPERATION_BODY_KINDS),
  response: z.enum(AI_OPERATION_RESPONSE_KINDS),
});
export type AiOperation = z.infer<typeof aiOperationSchema>;

export const aiOperationDetailSchema = aiOperationSchema.extend({
  /** JSON Schema (draft 2020-12) for spørringen, eller null når ruta ikke tar noen. */
  querySchema: z.record(z.string(), z.unknown()).nullable(),
  /** JSON Schema for JSON-kroppen, eller null. */
  bodySchema: z.record(z.string(), z.unknown()).nullable(),
  /** Gjentakelse med samme `Idempotency-Key` gir det samme svaret i stedet for en ny rad. */
  idempotent: z.boolean(),
  /** Felt som også tar veggklokke: `{ localDateTime, timeZone? }`. */
  localTimeFields: z.array(z.string()),
  /** MCP-verktøyet som kaller den; null for `never`. */
  tool: z.enum(['advanced_read', 'advanced_write']).nullable(),
});
export type AiOperationDetail = z.infer<typeof aiOperationDetailSchema>;

export const aiOperationsResponseSchema = z
  .object({
    items: z.array(aiOperationSchema),
    /** Treff i alt, også de som ikke fikk plass under `limit`. */
    total: z.number().int(),
    hasMore: z.boolean(),
    /** Uten `q`, `resource` og `operationId`: ressursene med antall operasjoner, så AI-en kan velge. */
    resources: z.array(
      z.object({
        name: z.string(),
        read: z.number().int(),
        write: z.number().int(),
        never: z.number().int(),
      }),
    ),
    /** Med `operationId`: operasjonen med skjemaene, eller null når den ikke finnes. */
    operation: aiOperationDetailSchema.nullable(),
  })
  .meta({ id: 'AiOperationsResponse' });
export type AiOperationsResponse = z.infer<typeof aiOperationsResponseSchema>;
